import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContentCard from "@/components/base/ContentCard";
import CharacterCard from "@/components/base/CharacterCard";
import { typeMeta, getByType, isContentType, uniqueValues, formatDate } from "@/lib/content";
import type { ContentItem, ContentType } from "@/mocks/content";

const PAGE_SIZE = 4;
const NEWS_STEP = 3;

type SortKey = "latest" | "hot";

function applyTimeRange(items: ContentItem[], range: "week" | "month" | "all"): ContentItem[] {
  if (range === "all") return items;
  const times = items.map((i) => new Date(i.date).getTime());
  const reference = Math.max(...times);
  const days = range === "week" ? 7 : 30;
  const limit = reference - days * 24 * 60 * 60 * 1000;
  return items.filter((i) => new Date(i.date).getTime() >= limit);
}

export default function CategoryPage() {
  const { type: rawType } = useParams<{ type: string }>();
  const type: ContentType | null = rawType && isContentType(rawType) ? rawType : null;

  const baseItems = useMemo(() => (type ? getByType(type) : []), [type]);

  const years = useMemo(
    () => uniqueValues(baseItems.map((i) => i.year).filter((v): v is string => Boolean(v))),
    [baseItems]
  );
  const seriesList = useMemo(
    () => uniqueValues(baseItems.map((i) => i.series).filter((v): v is string => Boolean(v))),
    [baseItems]
  );
  const groups = useMemo(
    () => uniqueValues(baseItems.map((i) => i.group).filter((v): v is string => Boolean(v))),
    [baseItems]
  );

  const [year, setYear] = useState("全部");
  const [series, setSeries] = useState("全部");
  const [group, setGroup] = useState("全部");
  const [timeRange, setTimeRange] = useState<"week" | "month" | "all">("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(NEWS_STEP);

  useEffect(() => {
    setYear("全部");
    setSeries("全部");
    setGroup("全部");
    setTimeRange("all");
    setSort("latest");
    setPage(1);
    setVisibleCount(NEWS_STEP);
  }, [type]);

  const filtered = useMemo(() => {
    let list = [...baseItems];
    if (type === "animation") {
      if (year !== "全部") list = list.filter((i) => i.year === year);
      if (series !== "全部") list = list.filter((i) => i.series === series);
      list.sort((a, b) =>
        sort === "latest"
          ? new Date(b.date).getTime() - new Date(a.date).getTime()
          : b.tags.length - a.tags.length
      );
    } else if (type === "character") {
      if (group !== "全部") list = list.filter((i) => i.group === group);
    } else if (type === "news") {
      list = applyTimeRange(list, timeRange);
      list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
    return list;
  }, [baseItems, type, year, series, group, timeRange, sort]);

  useEffect(() => {
    setPage(1);
    setVisibleCount(NEWS_STEP);
  }, [year, series, group, timeRange, sort]);

  if (!type) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 pt-28 pb-20 text-center md:px-8 md:pt-36">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">请从下方入口重新选择内容栏目。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-home-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const meta = typeMeta[type];
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const newsItems = filtered.slice(0, visibleCount);
  const hasFilter =
    year !== "全部" || series !== "全部" || group !== "全部" || timeRange !== "all";

  const resetFilters = () => {
    setYear("全部");
    setSeries("全部");
    setGroup("全部");
    setTimeRange("all");
  };

  const renderGrid = () => {
    if (filtered.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <i className="ri-filter-off-line text-4xl text-foreground-300" aria-hidden="true" />
          <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
            没有符合条件的内容
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            试试调整或重置筛选条件，浏览全部内容。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置筛选
          </button>
        </div>
      );
    }

    if (type === "character") {
      return (
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {pagedItems.map((item) => (
            <CharacterCard key={item.id} item={item} />
          ))}
        </div>
      );
    }

    if (type === "news") {
      return (
        <div className="flex flex-col gap-4">
          {newsItems.map((item) => (
            <ContentCard key={item.id} item={item} variant="horizontal" />
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {pagedItems.map((item) => (
          <ContentCard key={item.id} item={item} />
        ))}
      </div>
    );
  };

  const filterChip = (active: boolean) =>
    `rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
      active
        ? "bg-primary-500 text-background-50"
        : "bg-background-100 text-foreground-600 hover:bg-background-200"
    }`;

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-secondary-50">
        <div className="mx-auto w-full max-w-7xl px-4 pt-28 pb-10 md:px-8 md:pt-36 md:pb-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.label }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {meta.label}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {meta.description}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          {type === "animation" && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-400">年份</span>
                {["全部", ...years].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setYear(item)}
                    className={filterChip(year === item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-400">子系列</span>
                {["全部", ...seriesList].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setSeries(item)}
                    className={filterChip(series === item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-400">排序</span>
                <button
                  type="button"
                  onClick={() => setSort("latest")}
                  className={filterChip(sort === "latest")}
                >
                  <i className="ri-time-line mr-1" aria-hidden="true" />
                  最新发布
                </button>
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  className={filterChip(sort === "hot")}
                >
                  <i className="ri-fire-line mr-1" aria-hidden="true" />
                  热门程度
                </button>
              </div>
            </>
          )}

          {type === "character" && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-medium text-foreground-400">角色分类</span>
              {["全部", ...groups].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setGroup(item)}
                  className={filterChip(group === item)}
                >
                  {item}
                </button>
              ))}
            </div>
          )}

          {type === "news" && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-medium text-foreground-400">时间范围</span>
              {(
                [
                  { key: "week", label: "最近一周" },
                  { key: "month", label: "最近一个月" },
                  { key: "all", label: "全部" },
                ] as const
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTimeRange(item.key)}
                  className={filterChip(timeRange === item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-background-100 pt-3">
            <p className="text-xs text-foreground-500">
              共 <span className="font-bold text-primary-600">{filtered.length}</span> 条内容
            </p>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700 whitespace-nowrap"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选
              </button>
            )}
          </div>
        </div>

        {renderGrid()}

        {type === "news" && visibleCount < filtered.length && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + NEWS_STEP)}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-add-line" aria-hidden="true" />
              加载更多
            </button>
          </div>
        )}

        {type !== "news" && filtered.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-9 w-9 rounded-md text-sm font-medium transition-colors ${
                  p === page
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
            </button>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground-400">
          最新更新：{filtered.length > 0 ? formatDate(filtered[0].date) : "暂无"}
        </p>
      </div>
    </SiteLayout>
  );
}